/** Login, second factor and TOTP enrolment (§11). */

import { QRCodeSVG } from 'qrcode.react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Notice } from '../components/Notice';
import { useAuth } from '../lib/auth';

export function LoginPage() {
  const { t } = useTranslation();
  const { stage, error, login, verifyMfa, enrollMfa, confirmMfa, me, selectTenant } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [enrollment, setEnrollment] = useState<{ secret: string; otpauth_uri: string } | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  async function guard(action: () => Promise<void>) {
    setBusy(true);
    try {
      await action();
    } catch {
      /* the error code is surfaced through the auth context */
    } finally {
      setBusy(false);
    }
  }

  if (recoveryCodes) {
    return (
      <Card title={t('login.recoveryTitle')}>
        <p>{t('login.recoveryHint')}</p>
        <ul className="codes">
          {recoveryCodes.map((value) => (
            <li key={value}>
              <code>{value}</code>
            </li>
          ))}
        </ul>
        <button type="button" className="button" onClick={() => setRecoveryCodes(null)}>
          {t('login.recoveryDone')}
        </button>
      </Card>
    );
  }

  if (stage === 'mfa_enrollment') {
    return (
      <Card title={t('login.enrollTitle')}>
        <p>{t('login.enrollHint')}</p>
        {enrollment ? (
          <>
            <figure className="qr">
              <figcaption>{t('login.enrollQr')}</figcaption>
              <QRCodeSVG
                value={enrollment.otpauth_uri}
                size={256}
                marginSize={4}
                level="M"
                bgColor="#ffffff"
                fgColor="#000000"
                title={t('login.enrollQrAlt')}
              />
            </figure>
            <dl className="definition">
              <dt>{t('login.enrollSecret')}</dt>
              <dd>
                <code>{enrollment.secret}</code>
              </dd>
              <dt>{t('login.enrollUri')}</dt>
              <dd className="break">
                <code>{enrollment.otpauth_uri}</code>
              </dd>
            </dl>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void guard(async () => {
                  setRecoveryCodes(await confirmMfa(code));
                  setCode('');
                });
              }}
            >
              <label className="field">
                <span>{t('login.mfaCode')}</span>
                <input
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                />
              </label>
              <Notice tone="error" code={error} />
              <button type="submit" className="button" disabled={busy}>
                {t('login.mfaSubmit')}
              </button>
            </form>
          </>
        ) : (
          <button
            type="button"
            className="button"
            disabled={busy}
            onClick={() => void guard(async () => setEnrollment(await enrollMfa()))}
          >
            {t('login.enrollTitle')}
          </button>
        )}
      </Card>
    );
  }

  if (stage === 'mfa_required') {
    return (
      <Card title={t('login.mfaTitle')}>
        <p>{t('login.mfaHint')}</p>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void guard(() => verifyMfa(code));
          }}
        >
          <label className="field">
            <span>{t('login.mfaCode')}</span>
            <input
              value={code}
              onChange={(event) => setCode(event.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              required
            />
          </label>
          <Notice tone="error" code={error} />
          <button type="submit" className="button" disabled={busy}>
            {t('login.mfaSubmit')}
          </button>
        </form>
      </Card>
    );
  }

  if (stage === 'ready' && me && !me.active_tenant_id) {
    return (
      <Card title={t('login.selectTenant')}>
        <ul className="choice-list">
          {me.tenants.map((tenant) => (
            <li key={tenant.tenant_id}>
              <button
                type="button"
                className="button button--ghost"
                onClick={() => void selectTenant(tenant.tenant_id)}
              >
                {tenant.name} <span className="muted">· {tenant.role}</span>
              </button>
            </li>
          ))}
        </ul>
      </Card>
    );
  }

  return (
    <Card title={t('login.title')}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void guard(() => login(email, password));
        }}
      >
        <label className="field">
          <span>{t('login.email')}</span>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="username"
            required
          />
        </label>
        <label className="field">
          <span>{t('login.password')}</span>
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
          />
        </label>
        <Notice tone="error" code={error} />
        <button type="submit" className="button" disabled={busy}>
          {t('login.submit')}
        </button>
      </form>
    </Card>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="login">
      <div className="login__card">
        <header>
          <img
            className="login__card__logo"
            src="/logo-usolar.png"
            srcSet="/logo-usolar.png 1x, /logo-usolar@2x.png 2x"
            alt={t('app.name')}
            width={244}
            height={56}
          />
          <h1>{title}</h1>
        </header>
        {children}
      </div>
    </div>
  );
}
